/* eslint-disable */
// T-1015 — bidirectional BASED ON view. Builds a Map of <item_id, dev[]>
// from the loaded devs array. Two matching passes:
//   1. item_id pass (Convention #124, unchanged): devs with
//      `based_on === 'Original Item (Catalog)'` AND a populated
//      `based_on_data.item_id`.
//   2. ref pass (2026-07-08): devs whose `dev_number` (minus a single trailing
//      A/B/… suffix) matches an original's DEVELOPMENT ORDER REF
//      (`data.developmentOrder`, may be slash-separated e.g. "DA2722 / DO2248").
//      Rescues imported/manual devs that carry the ref in dev_number but have
//      an empty item_id — no data migration needed. Does NOT require the
//      based_on flag (the dev_number ref match is sufficient — Discovery §5).
//
// Used by:
//   - FrameDetailModal / ItemDetailModal "Linked Developments" (Feature A)
//   - useCatalogFilters reverse-lookup branch (Feature B)
//   - CatalogCard eyebrow "↩ N devs" (Feature X)
//
// Approach P: zero new queries, useMemo over already-loaded arrays. Operates
// only over the in-memory devs (which already exclude deleted rows — Conv #17).

import { useMemo } from 'react';

const CATALOG_TYPE = 'Original Item (Catalog)';

// Mirror normalize_dev_number: trim → lowercase → collapse internal whitespace.
const norm = (s) => String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

export function useLinkedDevsMap(devs, items) {
  return useMemo(() => {
    const map = new Map();
    if (!Array.isArray(devs)) return map;

    // Per-(item,dev) dedupe so a dev linked by both passes appears once.
    const linkKeys = new Set();
    const link = (itemId, dev) => {
      const k = `${itemId}::${dev.id}`;
      if (linkKeys.has(k)) return;
      linkKeys.add(k);
      if (!map.has(itemId)) map.set(itemId, []);
      map.get(itemId).push(dev);
    };

    // Pass 1 — item_id (Convention #124, unchanged behavior).
    for (const d of devs) {
      const basedOn = d.based_on || d.data?.based_on;
      if (basedOn !== CATALOG_TYPE) continue;
      const bod = d.based_on_data || d.data?.based_on_data;
      const itemId = bod?.item_id;
      if (!itemId || typeof itemId !== 'string') continue;
      link(itemId, d);
    }

    // Pass 2 — ref match. Build a ref-token → Set(item_id) index from items,
    // then link devs whose base ref (dev_number minus one trailing letter)
    // matches a token. Skipped cleanly when items is absent.
    if (Array.isArray(items) && items.length) {
      const refIndex = new Map();
      for (const it of items) {
        if (!it || !it.id) continue;
        const ref = it.data?.developmentOrder ?? it.developmentOrder;
        if (!ref) continue;
        for (const tok of String(ref).split('/')) {
          const t = norm(tok);
          if (!t) continue;
          if (!refIndex.has(t)) refIndex.set(t, new Set());
          refIndex.get(t).add(it.id);
        }
      }
      if (refIndex.size) {
        for (const d of devs) {
          const rawNum = d.dev_number ?? d.data?.dev_number;
          if (!rawNum) continue;
          // Strip a single trailing letter (DS2999A → DS2999), then normalize.
          const baseRef = norm(String(rawNum).trim().replace(/([A-Za-z])$/, ''));
          if (!baseRef) continue;
          const itemIds = refIndex.get(baseRef);
          if (!itemIds) continue;
          for (const itemId of itemIds) link(itemId, d);
        }
      }
    }

    // Sort each item's dev list by updated_at desc — most recent first.
    for (const list of map.values()) {
      list.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
    }
    return map;
  }, [devs, items]);
}
